import { BusinessClock } from '../../core/time/business-clock.service';
import { clockView } from '../../core/time/business-time';
import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberService } from '../../core/services/member.service';
import { NotificationService } from '../../core/services/notification.service';
import { StudioService } from '../../core/services/studio.service';
import { TransactionService } from '../../core/services/transaction.service';
import { PagerComponent } from '../../shared/components/pager.component';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';
import { notificationMatchesKeyword } from './notification-keyword';
import {
  canRemoveNotificationRequest,
  isBeforeNotificationDeadline,
  isEditableBeforePlan,
  resolveCreateTransactionDay,
} from './notification-deadline';
import { canProcessNotificationRequest, isSubscriptionRequest } from './notification-deadline';
import { subscriptionError } from '../../core/services/subscription.service';

const REQUEST_TYPE_OPTIONS = [
  { id: '0', name: 'ロッカー契約' }, { id: '1', name: 'ロッカー解約' },
  { id: '2', name: '店舗移籍' }, { id: '3', name: 'コース区分変更' },
  { id: '4', name: '休会' }, { id: '5', name: '退会' },
  { id: '6', name: '支払変更' }, { id: '7', name: '除名' },
  { id: '8', name: '除名解除' }, { id: '9', name: '保険適応' },
  { id: '10', name: '物販開始' }, { id: '11', name: '物販停止' },
];

const STATUS_OPTIONS = [
  { id: '0', name: '申請中' }, { id: '5', name: '移籍先確認中' },
  { id: '2', name: '確認済' }, { id: '3', name: '削除済' }, { id: '4', name: '期限切れ' },
];

function buildYearOptions(): { id: number; name: string }[] {
  const now = new JstDate();
  const opts = [];
  for (let y = 2015; y <= now.getFullYear() + 1; y++) {
    opts.push({ id: y, name: `${y}年` });
  }
  return opts;
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = String(i + 1).padStart(2, '0');
  return { id: month, name: `${i + 1}月` };
});

function requestTypeIdOf(notification: any): number | null {
  const value = notification?.requestTypeId ?? notification?.requestType;
  const rawId = value && typeof value === 'object' ? value.id : value;
  if (rawId === null || rawId === undefined || rawId === '') return null;
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
}

function normalizeNotification(notification: any, dayOfCreateTransaction: number): any {
  const requestTypeId = requestTypeIdOf(notification);
  const requestType = REQUEST_TYPE_OPTIONS.find(o => Number(o.id) === requestTypeId) ?? notification?.requestType;
  return {
    ...notification,
    requestTypeId,
    requestType,
    editableBeforePlan: requestTypeId === 4 && isEditableBeforePlan(notification, dayOfCreateTransaction),
  };
}

function requestTypeName(id: string | number | null | undefined): string {
  if (id === null || id === undefined || id === '') return '';
  return REQUEST_TYPE_OPTIONS.find(o => String(o.id) === String(id))?.name ?? String(id);
}

function formatDeliveryDay(value: any, emptyValue = '-'): string {
  if (value === null || value === undefined || value === '') return emptyValue;
  return Number(value) === 0 ? '月末' : `${value}日`;
}

@Component({
    selector: 'app-notification-member',
    imports: [RouterLink, FormsModule, DatePipe, PagerComponent, FlashMessageComponent],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> 一覧 <span>(全 {{ filteredItems.length }} 件)</span></span>
    </div>
    <div class="card-body">
      <!-- 旧版 notification*.html の card-body 先頭 <flash-message> 相当 -->
      <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
      <form novalidate>
        <div class="row">
          <div class="col-md-3 text-md-right">会員ID：</div>
          <div class="col-md-3 form-group">{{ member?.memberCode }}</div>
          <div class="col-md-3 text-md-right">会員氏名：</div>
          <div class="col-md-3 form-group">{{ member?.lastName }}&nbsp;{{ member?.firstName }}</div>
          <div class="col-md-3 text-md-right">店舗：</div>
          <div class="col-md-3 form-group">{{ member?.studio?.name }}</div>
          <div class="col-md-3 text-md-right">状態：</div>
          <div class="col-md-3 form-group">
            @if (member?.status == 0) { <span>在籍中</span> }
            @if (member?.status == 1) { <span>停止</span> }
            @if (member?.status == 2) { <span>停止（除名）</span> }
          </div>
          <div class="col-md-3 text-md-right">区分コース：</div>
          <div class="col-md-3 form-group">{{ member?.studioPlan?.name }}</div>
          <div class="col-md-3 text-md-right">支払方法：</div>
          <div class="col-md-3 form-group">
            @if (member?.payment?.paymentMethod == 0) { <span>口座振替</span> }
            @if (member?.payment?.paymentMethod == 1) { <span>クレジットカード</span> }
          </div>
          <div class="col-md-3 text-md-right">ロッカー：</div>
          <div class="col-md-3 form-group">
            @if (member?.lockerNumber == null) { <span>使用しない</span> }
            @if (member?.lockerNumber != null) { <span>{{ member?.lockerNumber?.product?.name }}</span> }
          </div>
          <div class="col-md-3 text-md-right">保険適応：</div>
          <div class="col-md-3 form-group">
            @if (member?.isInsured == 0) { <span>なし</span> }
            @if (member?.isInsured == 1) { <span>あり</span> }
          </div>
        </div>
        <hr>
        <div class="row">
          <div class="col-md-4">
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="request_type">申請種別</label>
                <select class="form-control" id="request_type" [(ngModel)]="requestTypeFilter" name="request_type" (ngModelChange)="pageIndex = 0">
                  <option [ngValue]="null">-- すべて --</option>
                  @for (opt of requestTypeOptions; track opt.id) {
                    <option [ngValue]="opt">{{ opt.name }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="status">状態</label>
                <select class="form-control" id="status" [(ngModel)]="statusFilter" name="status" (ngModelChange)="pageIndex = 0">
                  <option [ngValue]="null">-- すべて --</option>
                  @for (opt of statusOptions; track opt.id) {
                    <option [ngValue]="opt">{{ opt.name }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="col-form-label" for="request_month_year">申請年月</label>
              <div class="bisected-input-wrap">
                <div class="bisected-input">
                  <select class="form-control" id="request_month_year" [(ngModel)]="requestMonthYear" name="request_month_year" (ngModelChange)="onRequestMonthChange()">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of yearOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div><span>&nbsp;</span></div>
                <div class="bisected-input">
                  <select class="form-control" id="request_month_month" [(ngModel)]="requestMonthMonth" name="request_month_month" (ngModelChange)="onRequestMonthChange()">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of monthOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row or-conditions-box">
          <span class="box-title">オプション（OR条件）</span>
          <div class="col-md-4">
            <div class="form-group">
              <label class="col-form-label custom-control custom-checkbox" for="use_start_date">
                <input class="custom-control-input" [(ngModel)]="useStartDate" id="use_start_date" name="use_start_date" type="checkbox" (ngModelChange)="pageIndex = 0">
                <label class="custom-control-label" for="use_start_date">開始月</label>
              </label>
              <div class="bisected-input-wrap">
                <div class="bisected-input">
                  <select class="form-control" [(ngModel)]="startDateYear" name="start_date_year" [disabled]="!useStartDate" (ngModelChange)="pageIndex = 0">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of yearOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div><span>&nbsp;</span></div>
                <div class="bisected-input">
                  <select class="form-control" [(ngModel)]="startDateMonth" name="start_date_month" [disabled]="!useStartDate" (ngModelChange)="pageIndex = 0">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of monthOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="col-form-label custom-control custom-checkbox" for="use_end_date">
                <input type="checkbox" class="custom-control-input" [(ngModel)]="useEndDate" id="use_end_date" name="use_end_date" (ngModelChange)="pageIndex = 0">
                <label class="custom-control-label" for="use_end_date">終了月</label>
              </label>
              <div class="bisected-input-wrap">
                <div class="bisected-input">
                  <select class="form-control" [(ngModel)]="endDateYear" name="end_date_year" [disabled]="!useEndDate" (ngModelChange)="pageIndex = 0">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of yearOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div><span>&nbsp;</span></div>
                <div class="bisected-input">
                  <select class="form-control" [(ngModel)]="endDateMonth" name="end_date_month" [disabled]="!useEndDate" (ngModelChange)="pageIndex = 0">
                    <option [ngValue]="null">-- すべて --</option>
                    @for (opt of monthOptions; track opt.id) {
                      <option [ngValue]="opt">{{ opt.name }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row" style="align-items: flex-end;">
          <div class="col-md-8">
            <div class="form-group row">
              <div class="col-md-12">
                <label class="col-form-label" for="keyword">キーワード検索</label>
                <input id="keyword" name="keyword" type="text" [(ngModel)]="keyword"
                  class="form-control" placeholder="会員ID、会員氏名、申請種別など" (ngModelChange)="pageIndex = 0">
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group row">
              <div class="col-md-12">
                <button class="btn btn-warning text-white" type="button" (click)="clearFilter()">
                  <i class="fa s-icon-backspace"></i>&nbsp;条件クリア
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      <hr>
    </div>
    <div class="card-body">
      <div class="form-group row">
        <div class="col-md-12">
          <button class="btn btn-primary text-white" type="button" [routerLink]="['/notification', memberId, 'plan']" title="新規作成">
            <i class="fa s-icon-plus"></i>&nbsp;新規作成
          </button>
        </div>
      </div>
      @if (searching) {
        <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
      }
      @if (!searching && filteredItems.length === 0) {
        <span>該当するデータはありません</span>
      }
      @if (filteredItems.length > 0) {
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
        <div class="table-responsive parity-table-scroll">
<table class="parity-wide-table table table-bordered table-sm table-hover table-ptb-0">
          <thead>
            <tr>
              <th width="10%">状態</th>
              <th>店舗名</th>
              <th width="10%">会員ID</th>
              <th>会員氏名(かな)</th>
              <th>申請種別</th>
              <th>契約内容</th>
              <th>引渡日</th>
              <th>移籍先店舗名</th>
              <th>変更前コース</th>
              <th>変更後コース</th>
              <th>休会申請時コース</th>
              <th>休会中コース</th>
              <th>休会あけコース</th>
              <th>変更後支払種別</th>
              <th>開始月</th>
              <th>終了月</th>
              <th>申請日時</th>
            </tr>
          </thead>
          <tbody>
            @for (item of pagedItems; track item.id) {
              <tr>
                <td class="text-center">
                  @if (item.status == 0) {
                    <button class="btn text-danger p-0 bg-inherit active" type="button" (click)="openStatusModal(item)" title="申請中"><i class="fa fa-2x fa-bullhorn"></i></button>
                  }
                  @if (item.status == 1) {
                    <button class="btn text-warning bg-inherit p-0 active" type="button" (click)="openStatusModal(item)" title="確認中"><i class="fa fa-2x fa-edit"></i></button>
                  }
                  @if (item.status == 2 && (item.requestTypeId == 3 || item.requestTypeId == 5 || item.requestTypeId == 7)) {
                    <button class="btn text-success p-0 bg-inherit active" type="button" (click)="openStatusModal(item)" title="確認済"><i class="fa fa-2x s-icon-finished"></i></button>
                  }
                  @if (item.status == 2 && item.requestTypeId != 3 && item.requestTypeId != 5 && item.requestTypeId != 7) {
                    <button class="btn text-success p-0 bg-inherit active" type="button" title="確認済"><i class="fa fa-2x s-icon-finished"></i></button>
                  }
                  @if (item.status == 3) {
                    <button class="btn text-secondary p-0 bg-inherit active" type="button" title="削除済"><i class="fa fa-2x s-icon-close"></i></button>
                  }
                  @if (item.status == 4) {
                    <button class="btn text-secondary p-0 bg-inherit active" type="button" title="期限切れ"><i class="fa fa-2x fa-calendar-times-o"></i></button>
                  }
                  @if (item.status == 5) {
                    <button class="btn text-success bg-inherit p-0 active" type="button" (click)="openStatusModal(item, false)" title="移籍先確認中"><i class="fa fa-2x fa-sign-out"></i></button>
                  }
                </td>
                <td>{{ item.member?.studio?.name }}</td>
                <td>{{ item.member?.memberCode }}</td>
                <td>
                  <a href="" (click)="$event.preventDefault(); openMemberDetail(item.member)">
                    {{ item.member?.lastName }}&nbsp;{{ item.member?.firstName }}&#040;{{ item.member?.lastNameKana }}&nbsp;{{ item.member?.firstNameKana }}&#041;
                  </a>
                </td>
                <td>{{ requestTypeName(item.requestTypeId) }}</td>
                <td>
                  @if (item.requestTypeId == 10 || item.requestTypeId == 11) { {{ item.product?.name ?? '-' }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 10 || item.requestTypeId == 11) { {{ formatDeliveryDay(item.deliveryDay) }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 2) { {{ item.afterStudio?.name }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 3) { {{ item.beforeStudioPlan?.name }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 3) { {{ item.afterStudioPlan?.name }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 4) { {{ item.originalStudioPlan?.name }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 4) { {{ item.afterStudioPlan?.name }} }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 4) {
                    <span class="d-block">{{ item.beforeStudioPlan?.name }}</span>
                    @if (item.editableBeforePlan) {
                      <button type="button" class="mt-1 mb-1 btn btn-primary btn-sm text-white float-right" (click)="openEditBeforePlanModal(item)" title="編集"><i class="fa s-icon-edit fa-fw"></i></button>
                    }
                  }
                  @else { - }
                </td>
                <td>
                  @if (item.requestTypeId == 6 && item.afterPaymentMethod == 0) { 口座振替 }
                  @else if (item.requestTypeId == 6 && item.afterPaymentMethod == 1) { クレジットカード }
                  @else if (item.requestTypeId != 6) { - }
                </td>
                <td>{{ item.startDate | date:'yyyy年MM月' }}</td>
                <td>{{ item.endDate | date:'yyyy年MM月末' }}</td>
                <td>{{ item.createdAt | date:'yyyy/MM/dd HH:mm' }}</td>
              </tr>
            }
          </tbody>
        </table>
</div>
        <app-pager [pageIndex]="pageIndex" [totalPages]="totalPages" (pageChange)="goToPage($event)"></app-pager>
      }
    </div>
  </div>
</div>

<!-- 会員詳細モーダル -->
@if (showMemberDetailModal && selectedMember) {
  <div class="modal-backdrop fade show" (click)="showMemberDetailModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">会員情報 / 詳細</h5>
        </div>
        <div class="modal-body">
          <table class="table table-sm table-borderless">
            <tr><th class="text-right" style="width: 40%;">会員ID：</th><td>{{ selectedMember.memberCode }}</td></tr>
            <tr><th class="text-right">会員氏名（かな）：</th><td>{{ selectedMember.lastName }} {{ selectedMember.firstName }}({{ selectedMember.lastNameKana }} {{ selectedMember.firstNameKana }})</td></tr>
            <tr><th class="text-right">生年月日：</th><td>{{ selectedMember.birthday }}</td></tr>
            <tr><th class="text-right">住所：</th><td>〒{{ selectedMember.zip }}</td></tr>
            <tr><th class="text-right"></th><td>{{ selectedMember.province }}&nbsp;{{ selectedMember.city }}</td></tr>
            <tr><th class="text-right"></th><td>{{ selectedMember.area }}&nbsp;{{ selectedMember.address }}</td></tr>
            <tr><th class="text-right">電話番号：</th><td>{{ selectedMember.phone }}</td></tr>
            <tr><th class="text-right">メールアドレス：</th><td>{{ selectedMember.email }}</td></tr>
            <tr><th class="text-right">コース区分：</th><td>{{ selectedMember.studioPlan?.name }}</td></tr>
            <tr>
              <th class="text-right">支払方法：</th>
              <td>
                @if (selectedMember.payment?.paymentMethod == 0) { 口座振替 }
                @if (selectedMember.payment?.paymentMethod == 1) { クレジット }
              </td>
            </tr>
            <tr><th class="text-right">入退館キー：</th><td>{{ selectedMember.keyNumber?.keyNumber }}</td></tr>
            <tr><th class="text-right">ロッカー：</th><td>{{ selectedMember.lockerNumber?.lockerNumber }}</td></tr>
            <tr><th class="text-right">登録日時：</th><td>{{ selectedMember.createdAt | date:'yyyy/MM/dd HH:mm' }}</td></tr>
          </table>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showMemberDetailModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ステータス変更モーダル -->
@if (showStatusModal && selectedNotification) {
  <div class="modal-backdrop fade show" (click)="showStatusModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">諸届申請ステータスの変更</h5>
          <button type="button" class="close" (click)="showStatusModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <div class="custom-control custom-radio">
              @if (selectedMemberStatus == 1) {
                <label class="row">
                  <div class="col-md-3 custom-control custom-radio">
                    <input type="radio" id="status_radio0" class="custom-control-input" [(ngModel)]="changeStatus" [value]="0" name="changeStatus">
                    <label class="custom-control-label d-flex" for="status_radio0"><span>申請中&nbsp;</span></label>
                  </div>
                  <div class="col-md-2" style="margin-top: -5px;">
                    <label for="status_radio0"><i class="text-danger fa fa-3x fa-bullhorn"></i></label>
                  </div>
                  <div class="col-md-7">
                    <label for="status_radio0"><span>申請中に変更します。</span></label>
                  </div>
                </label>
                <hr>
              }
              @if (((selectedMemberStatus == 0 || selectedMemberStatus == 1) && !isMove) || (selectedMemberStatus == 5 && isMoveIn == true)) {
                <label class="row">
                  <div class="col-md-4 custom-control custom-radio">
                    <input type="radio" id="status_radio2" class="custom-control-input" [(ngModel)]="changeStatus" [value]="2" name="changeStatus">
                    <label class="custom-control-label" for="status_radio2"><span>確認済</span></label>
                  </div>
                  <div class="col-md-2" style="margin-top: -5px;">
                    <label for="status_radio2"><i class="text-success fa fa-3x s-icon-finished"></i></label>
                  </div>
                  <div class="col-md-6">
                    <label for="status_radio2"><span>確認済に変更します。</span></label>
                  </div>
                </label>
                <hr>
              }
              @if ((selectedMemberStatus == 0 || selectedMemberStatus == 1) && isMove) {
                <label class="row">
                  <div class="col-md-4 custom-control custom-radio">
                    <input type="radio" id="status_radio3" class="custom-control-input" [(ngModel)]="changeStatus" [value]="3" name="changeStatus">
                    <label class="custom-control-label" for="status_radio3"><span>移籍先確認中</span></label>
                  </div>
                  <div class="col-md-2" style="margin-top: -5px;">
                    <label for="status_radio3"><i class="text-success fa fa-3x fa-sign-out"></i></label>
                  </div>
                  <div class="col-md-6">
                    <label for="status_radio3"><span>移籍先確認中に変更します。</span></label>
                  </div>
                </label>
                <hr>
              }
              <label class="row">
                <div class="col-md-4 custom-control custom-radio">
                  <input type="radio" id="status_radio4" class="custom-control-input" [(ngModel)]="changeStatus" [value]="4" name="changeStatus">
                  <label class="custom-control-label" for="status_radio4"><span>削除済</span></label>
                </div>
                <div class="col-md-2" style="margin-top: -5px;">
                  <label for="status_radio4"><i class="text-danger fa fa-3x s-icon-delete"></i></label>
                </div>
                <div class="col-md-6">
                  <label for="status_radio4"><span>削除済に変更します。</span></label>
                </div>
              </label>
              <hr>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showStatusModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          @if (changeStatus == 0) {
            <button class="btn btn-primary text-white" type="button" (click)="toApplying(selectedNotification.id)"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
          @if (changeStatus == 2) {
            <button class="btn btn-primary text-white" type="button" (click)="toProcessed(selectedNotification)"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
          @if (changeStatus == 3) {
            <button class="btn btn-primary text-white" type="button" (click)="toMoveOut(selectedNotification)"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
          @if (changeStatus == 4) {
            <button class="btn btn-primary text-white" type="button" (click)="removeNotification(selectedNotification)"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
          @if (changeStatus == null || changeStatus == undefined) {
            <button class="btn btn-primary text-white" type="button" disabled><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- 休会あけコース変更モーダル -->
@if (showEditBeforePlanModal && selectedNotification) {
  <div class="modal-backdrop fade show" (click)="showEditBeforePlanModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">休会あけコースの変更</h5>
          <button type="button" class="close" (click)="showEditBeforePlanModal = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <div class="col-md-8">
              <p class="mb-1">会員氏名: {{ selectedNotification.member?.memberCode }} {{ selectedNotification.member?.lastName }} {{ selectedNotification.member?.firstName }}</p>
              <p>現休会あけコース: {{ selectedNotification.beforeStudioPlan?.name }}</p>
              <label class="col-form-label">変更後休会あけコース</label>
              <select class="form-control" [(ngModel)]="beforePlan" name="before_plan">
                <option [ngValue]="null">選択してください</option>
                @for (plan of studioPlans; track plan.id) {
                  <option [ngValue]="plan">{{ plan.name }}</option>
                }
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showEditBeforePlanModal = false"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          <button class="btn btn-primary text-white" type="button" (click)="editBeforePlan(selectedNotification)" [disabled]="!beforePlan"><i class="fa s-icon-turnOver"></i>&nbsp;変更</button>
        </div>
      </div>
    </div>
  </div>
}
  `
})
export class NotificationMemberComponent implements OnInit {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(true);

  private pageTitleService = inject(PageTitleService);
  private route = inject(ActivatedRoute);
  private memberService = inject(MemberService);
  private notificationService = inject(NotificationService);
  private studioService = inject(StudioService);
  private flash = inject(FlashService);
  private transactionService = inject(TransactionService);

  memberId!: number;
  member: any = null;
  studioPlans: any[] = [];
  items: any[] = [];
  keyword = '';
  requestTypeFilter: any = null;
  statusFilter: any = null;
  searching = false;

  requestMonthYear: any = null;
  requestMonthMonth: any = null;
  useStartDate = false;
  useEndDate = false;
  startDateYear: any = null;
  startDateMonth: any = null;
  endDateYear: any = null;
  endDateMonth: any = null;

  showStatusModal = false;
  showMemberDetailModal = false;
  showEditBeforePlanModal = false;
  selectedNotification: any = null;
  selectedMember: any = null;
  selectedMemberStatus: number | null = null;
  isMove = false;
  isMoveIn: boolean | null = null;
  changeStatus: number | null = null;
  beforePlan: any = null;

  readonly pageLimit = 300;
  pageIndex = 0;

  readonly yearOptions = buildYearOptions();
  readonly monthOptions = MONTH_OPTIONS;
  readonly requestTypeOptions = REQUEST_TYPE_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly requestTypeName = requestTypeName;
  readonly formatDeliveryDay = formatDeliveryDay;
  private dayOfCreateTransaction = 11;

  // Legacy empty-string filtering excludes null; clearFilter removes that filter.
  private requestMonthFilterEnabled = true;

  onRequestMonthChange(): void {
    this.requestMonthFilterEnabled = true;
    this.pageIndex = 0;
  }

  private get requestMonthFilter(): string {
    const y = this.requestMonthYear?.id ? String(this.requestMonthYear.id) : '';
    const m = this.requestMonthMonth?.id ? '-' + this.requestMonthMonth.id : '';
    return y + m;
  }

  get filteredItems(): any[] {
    let list = this.items;
    if (this.keyword) {
      list = list.filter(n => notificationMatchesKeyword(n, this.keyword));
    }
    if (this.requestTypeFilter != null) {
      list = list.filter(n => String(n.requestTypeId) === String(this.requestTypeFilter.id));
    }
    if (this.statusFilter != null) {
      list = list.filter(n => String(n.status) === String(this.statusFilter.id));
    }
    const rm = this.requestMonthFilter;
    if (this.requestMonthFilterEnabled || rm) {
      list = list.filter(n => n.requestMonth != null && String(n.requestMonth).includes(rm));
    }
    if (this.useStartDate || this.useEndDate) {
      list = list.filter(item => {
        let isStartInRange = true;
        let isEndInRange = true;
        if (this.useStartDate) {
          if (!item.startDate) {
            isStartInRange = !this.startDateYear && !this.startDateMonth;
          } else {
            const d = new JstDate(item.startDate);
            if (this.startDateYear) isStartInRange = d.getFullYear() === this.startDateYear.id;
            if (isStartInRange && this.startDateMonth) isStartInRange = d.getMonth() + 1 === Number(this.startDateMonth.id);
          }
        }
        if (this.useEndDate) {
          if (!item.endDate) {
            isEndInRange = !this.endDateYear && !this.endDateMonth;
          } else {
            const d = new JstDate(item.endDate);
            if (this.endDateYear) isEndInRange = d.getFullYear() === this.endDateYear.id;
            if (isEndInRange && this.endDateMonth) isEndInRange = d.getMonth() + 1 === Number(this.endDateMonth.id);
          }
        }
        return this.useStartDate && this.useEndDate
          ? isStartInRange || isEndInRange
          : isStartInRange && isEndInRange;
      });
    }
    return list;
  }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit);
  }
  get totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }
  goToPage(p: number): void {
    this.pageIndex = Math.max(0, Math.min(p, this.totalPages - 1));
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('会員管理', 's-icon-members');
    this.memberId = Number(this.route.snapshot.paramMap.get('memberId'));
    const schedule = await this.transactionService.getBatchSchedule().catch(() => null);
    this.dayOfCreateTransaction = resolveCreateTransactionDay(schedule);
    this.member = await this.memberService.getOne(this.memberId);
    if (this.member?.studio?.id) {
      const studioData = await this.studioService.list(this.member.studio.id);
      this.studioPlans = studioData?.studioPlans ?? [];
    }
    await this.loadNotifications();
  }

  private async loadNotifications(): Promise<void> {
    this.searching = true;
    try {
      const raw: any[] = await this.memberService.getNotification(this.memberId) ?? [];
      this.items = raw.map(item => normalizeNotification(item, this.dayOfCreateTransaction));
      this.goToPage(this.pageIndex);
    } finally {
      this.searching = false;
    }
  }

  clearFilter(): void {
    this.requestMonthFilterEnabled = false;
    this.pageIndex = 0;
    this.keyword = '';
    this.requestTypeFilter = null;
    this.statusFilter = null;
    this.requestMonthYear = null;
    this.requestMonthMonth = null;
    this.useStartDate = false;
    this.useEndDate = false;
    this.startDateYear = null;
    this.startDateMonth = null;
    this.endDateYear = null;
    this.endDateMonth = null;
  }

  openMemberDetail(member: any): void {
    this.selectedMember = member;
    this.showMemberDetailModal = true;
  }

  openStatusModal(item: any, isMoveIn: boolean | null = null): void {
    this.selectedNotification = item;
    this.selectedMemberStatus = item.status;
    this.isMove = Number(item.requestTypeId) === 2;
    this.isMoveIn = isMoveIn;
    this.changeStatus = null;
    this.showStatusModal = true;
  }

  openEditBeforePlanModal(item: any): void {
    this.selectedNotification = item;
    this.beforePlan = null;
    this.showEditBeforePlanModal = true;
  }

  async toApplying(id: number): Promise<void> {
    this.showStatusModal = false;
    await this.updateRequestStatus(this.selectedNotification ?? { id }, { id, status: 0 });
  }

  async toProcessed(notification: any): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    this.showStatusModal = false;
    if (!canProcessNotificationRequest(notification, this.dayOfCreateTransaction)) {
      this.showStatusError('締め日を過ぎた諸届は確認済みへ変更できません。再度申請を行ってください。');
      return;
    }
    await this.updateRequestStatus(notification, { id: notification.id, status: 2, acceptedAt: new JstDate() });
  }

  async toMoveOut(notification: any): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    this.showStatusModal = false;
    if (!isBeforeNotificationDeadline(notification, this.dayOfCreateTransaction)) {
      this.showStatusError('締め日を過ぎた諸届は移籍先確認中へ変更できません。再度申請を行ってください。');
      return;
    }
    await this.notificationService.upsert({ id: notification.id, status: 5, acceptedAt: new JstDate() });
    await this.loadNotifications();
  }

  async removeNotification(notification: any): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    this.showStatusModal = false;
    if (!canRemoveNotificationRequest(notification, this.dayOfCreateTransaction)) {
      this.showStatusError('締め日を過ぎた確認済の諸届は削除できません。');
      return;
    }
    await this.updateRequestStatus(notification, { id: notification.id, status: 3 });
  }

  async editBeforePlan(notification: any): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    if (!this.beforePlan) return;
    this.showEditBeforePlanModal = false;
    if (!isEditableBeforePlan(notification, this.dayOfCreateTransaction)) {
      this.showStatusError('締め日を過ぎた諸届は休会あけコースを変更できません。');
      return;
    }
    await this.notificationService.upsert({ id: notification.id, beforeStudioPlanId: this.beforePlan.id });
    await this.loadNotifications();
  }

  private updatingStatus = false;

  private async updateRequestStatus(notification: any, body: any): Promise<void> {
    if (this.updatingStatus) return;
    this.updatingStatus = true;
    try {
      const result = isSubscriptionRequest(notification)
        ? await this.notificationService.changeStatus(body)
        : await this.notificationService.upsert(body);
      if (typeof result === 'string') { this.showStatusError(result); return; }
      if (result !== null) await this.loadNotifications();
    } catch (error) { this.showStatusError(subscriptionError(error)); }
    finally { this.updatingStatus = false; }
  }

  // 旧版 notification.js の締め日超過 Flash.create('danger', …, 5000, true) と先頭scroll
  private showStatusError(message: string): void {
    this.flash.create('danger', message);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }
}
